/* eslint-disable jsx-a11y/anchor-is-valid */
import clsx from 'clsx'
import { KTSVG } from '../../../helpers'
import { useEffect, useState } from 'react'
import { Notification } from '../../../../app/pages/notification/Notification.types'
import {
  getNotifications,
  readAllNotifications,
  readNotification,
} from '../../../../app/pages/notification/Notification.requests'
import { useNavigate } from 'react-router-dom'
import { onForegroundMessage } from '../../../helpers/notification'

type Props = {
  toggleBtnClass?: string
  toggleBtnIconClass?: string
  menuPlacement?: string
  menuTrigger?: string
}

const NotificationsDropdown = ({
  toggleBtnClass = '',
  toggleBtnIconClass = 'svg-icon-2',
  menuPlacement = 'bottom-end',
  menuTrigger = "{default: 'click', lg: 'hover'}",
}: Props) => {
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)

  const fetchNotifications = async () => {
    try {
      const { data } = await getNotifications({})

      setNotifications(data.result)
      setUnreadCount(data.unread)
    } catch (error) {
      console.error('Error fetching notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  // Fetch notifications from API
  useEffect(() => {
    fetchNotifications()
  }, [])

  useEffect(() => {
    const unsubscribe = onForegroundMessage(fetchNotifications)
    return () => {
      unsubscribe()
    }
  }, [])

  const markAllAsRead = async () => {
    try {
      await readAllNotifications()
      setNotifications(notifications.map((n) => ({ ...n, read: true })))
      setUnreadCount(0)
    } catch (error) {
      console.error('Error marking notifications as read:', error)
    }
  }

  const markAsRead = async (id: string) => {
    try {
      await readNotification(id)
      setNotifications(
        notifications.map((n) => (n._id === id ? { ...n, read: true } : n))
      )
      setUnreadCount((prev) => prev - 1)
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  const handleNotificationClick = (notification: Notification) => () => {
    markAsRead(notification._id)
    if (notification.data.route === 'appointment' && notification.data.id)
      navigate(`/appointment/${notification.data.id}`)
  }

  return (
    <>
      {/* begin::Menu toggle */}
      <a
        href='#'
        className={clsx('btn btn-icon position-relative', toggleBtnClass)}
        data-kt-menu-trigger={menuTrigger}
        data-kt-menu-attach='parent'
        data-kt-menu-placement={menuPlacement}
      >
        <KTSVG
          path='/media/icons/duotune/general/gen007.svg'
          className={toggleBtnIconClass}
        />

        {unreadCount > 0 && (
          <span className='position-absolute bottom-100 start-100 translate-middle badge badge-circle badge-danger'>
            {unreadCount}
          </span>
        )}
      </a>
      {/* begin::Menu toggle */}

      {/* begin::Menu */}
      <div
        className='menu menu-sub menu-sub-dropdown menu-column w-350px w-lg-375px'
        data-kt-menu='true'
      >
        {/* begin::Heading */}
        <div className='d-flex flex-column bgi-no-repeat rounded-top bg-primary'>
          {/* begin::Title */}
          <h3 className='text-white fw-bold px-9 mt-5 mb-6'>
            Notifications
            <span className='fs-8 opacity-75 ps-3'>{unreadCount} unread</span>
          </h3>
          {/* end::Title */}
        </div>
        {/* end::Heading */}

        {/* begin::Tab content */}
        <div className='tab-content'>
          {/* begin::Tab panel */}
          <div
            className='tab-pane fade show active'
            id='kt_topbar_notifications_1'
            role='tabpanel'
          >
            {/* begin::Items */}
            <div className='scroll-y mh-325px my-0 px-3'>
              {loading ? (
                <div className='d-flex justify-content-center py-10'>
                  <div className='spinner-border text-primary' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                  </div>
                </div>
              ) : notifications.length === 0 ? (
                <div className='text-center py-10'>
                  <KTSVG
                    path='/media/icons/duotune/files/fil024.svg'
                    className='svg-icon-4x opacity-25'
                  />
                  <p className='text-gray-600 fw-semibold fs-5 mt-2'>
                    No notifications yet
                  </p>
                </div>
              ) : (
                notifications.map((notification) => (
                  <div
                    key={notification._id}
                    className={clsx('d-flex flex-stack my-2 p-2', {
                      'bg-light-primary rounded': !notification.read,
                    })}
                    onClick={handleNotificationClick(notification)}
                  >
                    {/* begin::Icon */}
                    <div className='symbol symbol-35px me-4'>
                      <span className='symbol-label'>
                        <KTSVG
                          // path={'/media/icons/duotune/general/gen007.svg'}
                          path={'/media/icons/duotune/ecommerce/ecm009.svg'}
                          className='svg-icon-2'
                        />
                      </span>
                    </div>
                    {/* end::Icon */}

                    {/* begin::Description */}
                    <div className='d-flex flex-column pe-3 w-100'>
                      <a
                        href='#'
                        className='fs-6 text-gray-800 text-hover-primary fw-bold'
                      >
                        {notification.title}
                      </a>
                      <span className='text-gray-500 fs-7'>
                        {notification.message}
                      </span>
                      <span className='text-gray-400 fs-8 mt-1'>
                        {' '}
                        {new Date(notification.createdAt).toLocaleString(
                          'en-GB',
                          {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true,
                          }
                        )}
                      </span>
                    </div>
                    {/* end::Description */}

                    {/* begin::Indicator */}
                    {!notification.read && (
                      <div className='ms-2'>
                        <span className='badge badge-xsm badge-circle badge-primary'></span>
                      </div>
                    )}
                    {/* end::Indicator */}
                  </div>
                ))
              )}
            </div>
            {/* end::Items */}

            {/* begin::Footer */}
            <div className='py-3 text-center border-top'>
              <a
                href='#'
                className='btn btn-color-gray-600 btn-active-color-primary'
                onClick={markAllAsRead}
              >
                Mark All as Read
              </a>
              <a
                href='#'
                onClick={() => navigate('/notifications')}
                className='btn btn-color-gray-600 btn-active-color-primary'
              >
                View All Notifications
              </a>
            </div>
            {/* end::Footer */}
          </div>
          {/* end::Tab panel */}
        </div>
        {/* end::Tab content */}
      </div>
      {/* end::Menu */}
    </>
  )
}

export { NotificationsDropdown }
